import {
  AfterContentInit, ChangeDetectionStrategy,
  Component,
  ContentChildren, ElementRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  Output,
  QueryList,
  SimpleChanges, TemplateRef, ChangeDetectorRef, AfterViewInit
} from "@angular/core";
import {DsTabContentComponent} from "./tab-content/ds-tab-content.component";
import {DsTabHeadComponent} from "./tab-head/ds-tab-head.component";
import {Subscription} from "rxjs/internal/Subscription";
import {NgTemplateOutlet} from "@angular/common";

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ds-tabs',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './ds-tabs.component.html',
    styleUrls: ['./ds-tabs.component.scss'],
    imports: [
        NgTemplateOutlet
    ]
})
export class DsTabsComponent implements AfterContentInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() activeTabId: string;
  @Input() withBorder: boolean = false;
  @Input() beforeContent?: TemplateRef<any>;
  @Output() onActiveTabChange: EventEmitter<{ oldTabId: string, tabId: string }>;

  @ContentChildren(DsTabHeadComponent) tabHeads: QueryList<DsTabHeadComponent>;
  @ContentChildren(DsTabContentComponent) tabContents: QueryList<DsTabContentComponent>;

  // Sliding indicator properties
  public indicatorPosition: number = 0;
  public indicatorWidth: number = 0;

  // Total width of tabs container
  public tabsContainerWidth: number = 248; // Initialize with fallback value

  private _subscriptions: Subscription[];
  private _tabHeadClickSubscriptions: Subscription[];
  public hasContent: boolean = false;

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {
    this.onActiveTabChange = new EventEmitter<{ oldTabId: string, tabId: string }>();
    this._subscriptions = [];
  }

  ngAfterContentInit() {
    this.initTabHeads();
    this.addEventHandlers();
  }

  private initTabHeads(): void {
    if (this.tabHeads && this.tabHeads.length) {
      const visibleTabIds = this.tabHeads
        .filter((tabHead: DsTabHeadComponent) => !tabHead.hideTab)
        .map((tabHead: DsTabHeadComponent) => tabHead.tabId);

      const validActiveTabId: string = this.getValidActiveTabId(this.activeTabId, visibleTabIds);
      if (this.activeTabId !== validActiveTabId) {
        this.updateActiveTabId(validActiveTabId);
      } else {
        this.updateChildrenActiveTabId(validActiveTabId);
        this.updateIndicator();
      }

      this.addTabHeadClickSubscriptions();
    }
  }

  private getValidActiveTabId(activeTabId: string, tabHeadIds: string[]): string {
    return tabHeadIds.indexOf(activeTabId) !== -1 ? activeTabId : (tabHeadIds.length > 0 ? tabHeadIds[0] : null);
  }

  private addEventHandlers(): void {
    this._subscriptions.push(this.tabHeads.changes.subscribe((): void => {
      this.removeTabHeadClickSubscriptions();
      this.initTabHeads();
      this.updateIndicator();
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['activeTabId']) {
      this.updateChildrenActiveTabId(this.activeTabId);
      this.updateIndicator();
    }
  }

  // Helper method to get only visible tabs
  private getVisibleTabs(): DsTabHeadComponent[] {
    if (!this.tabHeads) return [];

    return this.tabHeads.filter((tabHead: DsTabHeadComponent) => {
      if (!tabHead.elementRef || tabHead.hideTab) return false;
      const tabElement = tabHead.elementRef.nativeElement;
      const computedStyle = window.getComputedStyle(tabElement);
      return computedStyle.display !== 'none';
    });
  }

  private updateIndicator(): void {
    if (!this.tabHeads?.length) return;

    const activeTabHead = this.tabHeads.find(tabHead => tabHead.tabId === this.activeTabId);
    if (!activeTabHead?.elementRef || activeTabHead.hideTab) {
      this.indicatorPosition = 0;
      this.indicatorWidth = 0;
      this.cdr.detectChanges();
      return;
    }

    const tabHeadElement = activeTabHead.elementRef.nativeElement;
    const tabsContainer = this.elementRef.nativeElement.querySelector('.ds-tabs__heads > div');
    if (!tabHeadElement || !tabsContainer) return;

    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = tabHeadElement.getBoundingClientRect();

    this.indicatorPosition = tabRect.left - containerRect.left;
    this.indicatorWidth = tabRect.width;

    // Update container width
    let totalWidth = 0;
    const gap = 22; // 22px gap as defined in CSS
    const visibleTabs = this.getVisibleTabs();

    visibleTabs.forEach((tabHead, index) => {
      if (tabHead.elementRef) {
        totalWidth += tabHead.elementRef.nativeElement.offsetWidth;
        if (index < visibleTabs.length - 1) totalWidth += gap;
      }
    });

    this.tabsContainerWidth = totalWidth;
    this.cdr.detectChanges();
  }

  public updateActiveTabId(tabId: string): void {
    if (tabId !== this.activeTabId) {
      const oldTabId: string = this.activeTabId;
      this.activeTabId = tabId;

      this.onActiveTabChange.emit({
        oldTabId: oldTabId,
        tabId: this.activeTabId
      });

      this.updateChildrenActiveTabId(this.activeTabId);
      this.updateIndicator();
    }
  }

  private updateChildrenActiveTabId(tabId: string): void {
    this.tabHeads?.forEach((tabHead: DsTabHeadComponent): void => {
      if (tabHead.activeTabId !== tabId) {
        tabHead.activeTabId = tabId;
      }
    });

    this.tabContents?.forEach((tabContent: DsTabContentComponent): void => {
      if (tabContent.activeTabId !== tabId) {
        tabContent.activeTabId = tabId;
      }
    });
  }

  ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    this.removeTabHeadClickSubscriptions();
    this._subscriptions?.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this._subscriptions = null;
  }

  private removeTabHeadClickSubscriptions(): void {
    this._tabHeadClickSubscriptions?.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this._tabHeadClickSubscriptions = null;
  }

  private addTabHeadClickSubscriptions(): void {
    this._tabHeadClickSubscriptions = [];
    this.tabHeads.forEach((tabHead: DsTabHeadComponent): void => {
      this._tabHeadClickSubscriptions.push(tabHead.onTabHeadClick.subscribe(($event): void => {
        this.onTabHeadClick($event);
      }));
    });
  }

  public onTabHeadClick($event): void {
    this.updateActiveTabId($event.tabId);
  }

  ngAfterViewInit() {
    this.updateIndicator();
  }
}
